'use client'
import Footer from '@/src/components/commons/Footer/Footer'
import styles from './BlogDetail.module.scss'
import BackArrow from '@/public/assets/images/back_white_arrow.svg'
import Image from 'next/image'
import { useQuery } from '@apollo/client/react'
import { BlogGraphQLResponse } from '../Blogs.interface'
import { BLOG_SEARCH_QUERY } from '../Blogs.graphql'
import { usePathname } from 'next/navigation'
import { Fragment, ReactNode } from 'react'
import Link from 'next/link'
import Skeleton from '@/src/components/commons/generics/Skeleton/Skeleton'
import Calender from '@/public/assets/images/calender.svg'
import { getFormattedDate } from '@/src/utils/common'
import SocialShareButtons from '@/src/components/commons/generics/SocialShareButtons/SocialShareButtons'
import { getBlogCanonicalOrigin } from '@/src/utils/publicSiteUrl'
import DOMPurify from 'isomorphic-dompurify'
import parse from 'html-react-parser'

const BlogDetail = () => {
  const path = usePathname()

  const { data, loading, error } = useQuery<BlogGraphQLResponse>(BLOG_SEARCH_QUERY, {
    variables: { limit: 1, offset: 0, path },
  })

  const { data: recommendPost } = useQuery<BlogGraphQLResponse>(BLOG_SEARCH_QUERY, {
    variables: { limit: 10, offset: 0 },
  })

  if (error) {
    return (
      <>
        <BlogDetailContainer>
          <p className="text-white text-[20px] mt-[20px]">Unable to load data...</p>
        </BlogDetailContainer>
        <Footer />
      </>
    )
  }

  if (data || loading) {
    return (
      <>
        <BlogDetailContainer>
          <div className="pt-[50px] grid gap-[50px] grid-cols-1 lg:grid-cols-3">
            {loading ? (
              <LoadingInfo />
            ) : (
              <>
                <div className="lg:col-span-2">
                  <BlogDetailInfo
                    blogData={data}
                    sharePageUrl={`${getBlogCanonicalOrigin()}${path}`}
                  />
                </div>
                <div className="lg:col-span-1">
                  <MetaInfo info={data} rpInfo={recommendPost} />
                </div>
              </>
            )}
          </div>
        </BlogDetailContainer>
        <Footer />
      </>
    )
  }
}

export default BlogDetail

const BlogDetailContainer = ({ children }: { children: ReactNode }) => {
  const backText = 'Return to search results'
  const backUrl = '/blogs'
  return (
    <div className={`${styles.BlogDetailContainer} flex justify-center`}>
      <div className={`${styles.BlogDetailInnerCont} w-full h-full py-[70px] px-[24px] lg:px-0`}>
        <Link href={backUrl || '/'}>
          <div className="flex gap-[10px] items-center">
            <Image src={BackArrow} alt="" />
            <p className="text-white text-[16px] font-[500] leading-[16px] font-gt-haptik">
              {backText}
            </p>
          </div>
        </Link>
        {children}
      </div>
    </div>
  )
}

const BlogDetailInfo = ({
  blogData,
  sharePageUrl,
}: {
  blogData: BlogGraphQLResponse
  sharePageUrl: string
}) => {
  const blogInfo = blogData?.blogsGraphql?.entities?.[0]

  const wysiwygContents = blogInfo?.fieldPageParagraph?.flatMap((p) =>
    (p.entity.fieldReusableParagraphs || [])
      .map((rp) => rp.entity.fieldWysiwygModContent?.value)
      .filter(Boolean),
  )

  return (
    <>
      <div className="flex flex-col gap-[50px]">
        <div className="flex flex-col">
          <Image
            src={blogInfo?.fieldCoverImage?.entity?.url}
            alt={blogInfo?.fieldCoverImage?.alt}
            title={blogInfo?.fieldCoverImage?.title}
            width={785}
            height={400}
            className="w-full h-auto mb-[30px]"
          />
          <p
            className={`${styles?.date} font-gt-haptik text-[12px] font-[500] leading-[13px] tracking-[2.4px] uppercase pb-[10px]`}
          >
            {getFormattedDate(blogInfo?.publishedAt?.value)}
          </p>
          <h1 className="font-gt-haptik text-white text-[36px] font-[500] leading-[43px] tracking-[-0.36px] pb-[30px]">
            {blogInfo?.title}
          </h1>
          <div className={styles?.blogDetailLine}></div>
          <div className="py-[20px]">
            <SocialShareButtons
              title={'SHARE'}
              url={sharePageUrl}
              className={styles.socialShareButtons}
            />
          </div>
          <div className={styles?.blogDetailLine}></div>
        </div>
        <div className={`${styles.wysiwyg} flex flex-col font-gt-haptik`}>
          {wysiwygContents?.map((item, i: number) => (
            <Fragment key={i}>
              <div>{typeof item === 'string' ? parse(DOMPurify.sanitize(item)) : null}</div>
              <br />
            </Fragment>
          ))}
        </div>
      </div>
    </>
  )
}

const MetaInfo = ({ info, rpInfo }: { info: BlogGraphQLResponse; rpInfo: BlogGraphQLResponse }) => {
  const dataInfo = info?.blogsGraphql?.entities?.[0]
  const rpInfoData = rpInfo?.blogsGraphql?.entities
  const authors = [
    dataInfo?.entityOwner?.fieldName,
    ...(dataInfo?.fieldCoAuthors ?? []).map((author) => author?.entity?.fieldName),
  ]
  const tags = [
    dataInfo?.fieldBlogMainCategory?.entity?.name,
    ...(dataInfo?.fieldBlogSubCategory ?? []).map((category) => category?.entity?.name),
  ]
  const currentNid = [dataInfo?.nid]
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-[10px] font-gt-haptik">
        <p className="text-white text-[22px] font-[500] leading-[24px]">Authors</p>
        {authors?.map((item, index) => {
          const encoded = encodeURIComponent(item)
          return (
            <Link
              key={item ?? `author-${index}`}
              href={`/blogs?authors=${encoded}`}
              className="text-white text-[14px] font-[400] leading-[21px] tracking-[0.28px] underline cursor-pointer opacity-70 text-white!"
            >
              {item}
            </Link>
          )
        })}
      </div>

      <div className={styles?.line}></div>

      <div className="flex flex-col gap-[10px]">
        <p className="font-gt-haptik text-white text-[22px] font-[500] leading-[24px]">Tags</p>
        {tags?.map((item, index) => {
          const encoded = encodeURIComponent(item)
          return (
            <Link
              key={item ?? `tag-${index}`}
              href={`/blogs?tag=${encoded}`}
              className={
                'font-dm-mono text-[14px] font-[400] leading-[17.5px] tracking-[1.4px] uppercase cursor-pointer text-white!'
              }
            >
              <span className={`${styles.tag}`}>{item}</span>
            </Link>
          )
        })}
      </div>

      <div className={styles?.line}></div>

      <div className="flex flex-col gap-[20px]">
        <p className="font-gt-haptik text-white text-[22px] font-[500] leading-[24px]">
          Recommended for You
        </p>

        {rpInfoData
          ?.filter((item) => !currentNid.includes(item.nid))
          .map((item, index) => (
            <div key={index} className="flex flex-col gap-[10px] font-dm-mono">
              <div className={`${styles?.accent} flex gap-[13px]`}>
                <Image src={Calender} alt="" />
                <p className="text-[14px] font-[400] leading-[17.5px] tracking-[1.4px] uppercase">
                  {getFormattedDate(item?.publishedAt?.value)}
                </p>
              </div>
              <Link href={item?.path?.alias || '/'}>
                <p className="text-white text-[16px] font-[400] leading-[24px] tracking-[-0.8px] cursor-pointer">
                  {item?.title}
                </p>
              </Link>
              <div className={`${styles?.rpLine}`}></div>
            </div>
          ))}
      </div>
    </div>
  )
}

const LoadingInfo = () => {
  return (
    <>
      <div className="lg:col-span-2">
        <Skeleton height="20rem" count={1} />
        <br />
        <br />
        <Skeleton height="20rem" count={1} />
      </div>
      <div className="lg:col-span-1">
        <Skeleton height="2rem" count={1} />
        <Skeleton height="2rem" count={1} />
        <Skeleton height="2rem" count={1} />
        <br />
        <Skeleton height="2rem" count={1} />
        <Skeleton height="2rem" count={1} />
        <br />
        <Skeleton height="4rem" count={1} />
        <Skeleton height="4rem" count={1} />
        <Skeleton height="4rem" count={1} />
        <Skeleton height="4rem" count={1} />
        <Skeleton height="4rem" count={1} />
        <br />
      </div>
    </>
  )
}
